import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, MapPin, Waves, Mountain, Star } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const HomePage = () => {
  const { t } = useLanguage();

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section
        className="relative min-h-screen flex items-center justify-center"
        style={{
          backgroundImage: 'url(https://customer-assets-rejwkqb3.emergentagent.net/job_adriatic-escape-3/artifacts/tpfg5om2_WhatsApp%20Image%202026-07-21%20at%2012.07.52.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-black/45"></div>
        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p data-testid="hero-overline" className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase mb-4">
              {t('home.overline')}
            </p>
            <h1
              data-testid="hero-title"
              className="text-4xl sm:text-5xl lg:text-6xl text-white font-serif tracking-tight leading-none mb-6"
              style={{ textShadow: '2px 2px 8px rgba(0,0,0,0.3)' }}
            >
              {t('home.title')}
            </h1>
            <p
              data-testid="hero-subtitle"
              className="text-lg sm:text-xl text-white/90 mb-8 leading-relaxed max-w-2xl mx-auto"
              style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.3)' }}
            >
              {t('home.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/appartamenti"
                data-testid="hero-cta-discover"
                className="bg-[#2C5263] text-white px-8 py-4 rounded-md text-base font-medium hover:bg-[#1A3644] transition-colors inline-flex items-center justify-center gap-2"
              >
                {t('home.ctaDiscover')}
                <ChevronRight size={20} />
              </Link>
              <Link
                to="/prenota"
                data-testid="hero-cta-book"
                className="border-2 border-white text-white px-8 py-4 rounded-md text-base font-medium hover:bg-white/10 transition-colors inline-flex items-center justify-center"
              >
                {t('home.ctaBook')}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Perché Sceglierci */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase mb-2">{t('home.whyOverline')}</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif tracking-tight text-[#1A1A1A]">
              {t('home.whyTitle')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              data-testid="feature-venice"
              className="col-span-1 md:col-span-2 bg-[#F9F6F0] p-12 rounded-lg border border-[#2C5263]/10 hover:-translate-y-1 transition-transform"
            >
              <MapPin size={40} className="text-[#2C5263] mb-4" />
              <h3 className="text-2xl font-serif mb-4 text-[#1A1A1A]">{t('home.venice.title')}</h3>
              <p className="text-[#666666] leading-relaxed">{t('home.venice.text')}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              data-testid="feature-beach"
              className="bg-[#F9F6F0] p-12 rounded-lg border border-[#2C5263]/10 hover:-translate-y-1 transition-transform"
            >
              <Waves size={40} className="text-[#2C5263] mb-4" />
              <h3 className="text-2xl font-serif mb-4 text-[#1A1A1A]">{t('home.beach.title')}</h3>
              <p className="text-[#666666] leading-relaxed">{t('home.beach.text')}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              data-testid="feature-tranquility"
              className="bg-[#F9F6F0] p-12 rounded-lg border border-[#2C5263]/10 hover:-translate-y-1 transition-transform"
            >
              <Mountain size={40} className="text-[#2C5263] mb-4" />
              <h3 className="text-2xl font-serif mb-4 text-[#1A1A1A]">{t('home.tranquility.title')}</h3>
              <p className="text-[#666666] leading-relaxed">{t('home.tranquility.text')}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              data-testid="feature-location"
              className="col-span-1 md:col-span-2 bg-[#F9F6F0] p-12 rounded-lg border border-[#2C5263]/10 hover:-translate-y-1 transition-transform"
            >
              <Star size={40} className="text-[#D4AF37] mb-4" />
              <h3 className="text-2xl font-serif mb-4 text-[#1A1A1A]">{t('home.location.title')}</h3>
              <p className="text-[#666666] leading-relaxed">{t('home.location.text')}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-24 bg-[#F9F6F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase mb-2">{t('home.overview.overline')}</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif tracking-tight text-[#1A1A1A] mb-4">
              {t('home.overview.title')}
            </h2>
            <p className="text-base text-[#666666] max-w-2xl mx-auto leading-relaxed">
              {t('home.overview.subtitle')}
            </p>
          </div>
          <div className="text-center">
            <Link
              to="/appartamenti"
              data-testid="view-all-properties-link"
              className="bg-[#2C5263] text-white px-8 py-4 rounded-md text-base font-medium hover:bg-[#1A3644] transition-colors inline-flex items-center gap-2"
            >
              {t('home.overview.cta')}
              <ChevronRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="py-24 relative"
        style={{
          backgroundImage: 'url(https://customer-assets-rejwkqb3.emergentagent.net/job_adriatic-escape-3/artifacts/mn8pj4ar_Bozza%202_6%20-%20Photo.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <h2
            data-testid="cta-title"
            className="text-3xl sm:text-4xl lg:text-5xl font-serif text-white mb-6"
            style={{ textShadow: '2px 2px 8px rgba(0,0,0,0.3)' }}
          >
            {t('home.finalCta.title')}
          </h2>
          <p className="text-lg text-white/90 mb-8 leading-relaxed" style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.3)' }}>
            {t('home.finalCta.subtitle')}
          </p>
          <Link
            to="/prenota"
            data-testid="cta-book-button"
            className="bg-[#D4AF37] text-white px-10 py-4 rounded-md text-base font-medium hover:bg-[#C49F2F] transition-colors inline-flex items-center gap-2"
          >
            {t('home.finalCta.button')}
            <ChevronRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
